import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import WappIcono from "../wappIcono";
import { entregarArchivo, guardarParaVer, puedeDescargar } from "../../utils/entregarArchivo";

// WappCell / WappDocs para ver adjuntos de oficina; se carga solo la primera vez que se necesita.
const EditorOficina = lazy(() => import("./visorOficina"));

/*
	VISOR DE ARCHIVOS DE WAPP: comprobantes, identificaciones, contratos, facturas, capturas de un
	comentario, fotos de almacén. Todo se ve AQUÍ MISMO, encima de la pantalla, sin abrir otra
	ventana; al cerrarlo el usuario sigue exactamente donde estaba (scroll, modal abierto).

	Historia: se bajaba el archivo y luego window.open(blob). En celular eso nunca abría (un
	window.open que sale de una promesa no cuenta como toque del usuario y el navegador lo bloquea
	sin avisar), y en computadora abría otra pestaña, que a Esteban le resultaba raro.
	(Esteban, 2026-10-03: "los attachments no abren en celular" / "en pc también misma ventana")

	- Imagen: se enseña completa.
	- PDF en computadora: se enseña embebido, con su barra de páginas/zoom/imprimir.
	- PDF en teléfono: sus páginas dibujadas aquí mismo con pdf.js (2026-10-20); si no se puede, botón "Abrir PDF".
	- Otro tipo: botón "Descargar" (liga tocada por el usuario, que nunca se bloquea).
	- Excel, Word, PowerPoint (2026-10-12, Christy no podía abrir un .xlsx "Liquidación de pagos"): se
	  abren en WappCell / WappDocs aquí mismo. Antes decía "no tiene vista previa" o bajaba un archivo
	  sin nombre ni extensión que la computadora no sabía abrir.
*/

export type ArchivoCargado = Blob | { blob: Blob; nombre?: string | null };

export interface ArchivoParaVer {
	nombre: string;
	/** Trae el archivo del servidor (con la sesión). Puede devolver también el nombre real. */
	cargar: () => Promise<ArchivoCargado>;
	/** Si la imagen ya está cargada (miniatura), su URL: se enseña al instante. */
	urlLista?: string | null;
	/** Video: se reproduce desde esta URL (firmada) sin bajarlo entero antes. */
	video?: boolean;
	/** URL directa (firmada) en vez de bajar el archivo: fotos y videos se ven sin descargarse completos. */
	obtenerUrl?: () => Promise<string>;
	/** Tipo del archivo (image/png, video/mp4, application/pdf…) para saber cómo enseñarlo con obtenerUrl. */
	mime?: string | null;
	/** Galería (WappTeam, 2026-10-09): los demás archivos de la conversación para pasar con ← →. */
	galeria?: ArchivoParaVer[];
	indice?: number;
	/** false = sin botón Descargar. */
	descargar?: boolean;
	/** Módulo de donde viene (carpeta de WappDrive al guardarlo): "Facturas", "Comprobantes de pago"… */
	origen?: string;
}

const EVENTO = "wapp:ver-archivo";

// Same pdf.js build as the public signing page (firma.tsx); its worker is served from public/vendor.
const PDF_WORKER = "/vendor/pdfjs-3.11.174/pdf.worker.min.js";

/**
 * PDF on a phone, drawn page by page right here (Esteban, 2026-10-20: opening a contract on the phone first showed an
 * "Abrir PDF" button and only then the PDF; it should open at once). Phones can't embed a PDF in an iframe, so pdf.js
 * paints each page on a canvas. If it fails, onFallo brings back the "Abrir PDF" button.
 */
const PaginasPdf: React.FC<{ url: string; onFallo: () => void }> = ({ url, onFallo }) => {
	const caja = useRef<HTMLDivElement>(null);
	const [cargando, setCargando] = useState(true);
	useEffect(() => {
		let vivo = true;
		let doc: any = null;
		(async () => {
			try {
				const pdfjs: any = await import("pdfjs-dist/legacy/build/pdf");
				pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER;
				doc = await pdfjs.getDocument({ url }).promise;
				const ancho = Math.min(caja.current?.clientWidth || 360, 900);
				const ratio = Math.min(window.devicePixelRatio || 1, 2);
				for (let i = 1; i <= doc.numPages && vivo; i++) {
					const pagina = await doc.getPage(i);
					const base = pagina.getViewport({ scale: 1 });
					const vista = pagina.getViewport({ scale: (ancho / base.width) * ratio });
					const canvas = document.createElement("canvas");
					canvas.width = vista.width;
					canvas.height = vista.height;
					canvas.setAttribute("aria-label", "Página " + i + " de " + doc.numPages);
					await pagina.render({ canvasContext: canvas.getContext("2d"), viewport: vista }).promise;
					if (!vivo) return;
					caja.current?.appendChild(canvas);
					if (i === 1) setCargando(false);
				}
			} catch {
				if (vivo) { setCargando(false); onFallo(); }
			}
		})();
		return () => { vivo = false; if (doc) doc.destroy(); };
	}, [url, onFallo]);
	return (
		<div className="waVisorPaginas" ref={caja} onClick={(e) => e.stopPropagation()}>
			{cargando ? <div className="waVisorCaja">Cargando…</div> : null}
		</div>
	);
};

const esTelefono = () => typeof window !== "undefined"
	&& (window.matchMedia("(max-width: 768px)").matches || /iPhone|iPod|Android/i.test(navigator.userAgent));

/** Abre el visor. Se puede llamar desde cualquier lado (no abre ventanas, así que nada lo bloquea). */
export const abrirArchivo = (archivo: ArchivoParaVer): Promise<void> => {
	window.dispatchEvent(new CustomEvent(EVENTO, { detail: archivo }));
	return Promise.resolve();
};

type Clase = "imagen" | "pdf" | "video" | "oficina" | "otro";

const OFICINA_EXT = /\.(xlsx|xls|ods|csv|docx|doc|odt|rtf|pptx|ppt|odp)$/i;
const OFICINA_MIME = /spreadsheet|ms-excel|wordprocessing|msword|presentation|ms-powerpoint|opendocument|text\/csv|rtf/i;

const claseDe = (tipo: string, nombre: string): Clase => {
	if (tipo.startsWith("video/")) {
		return "video";
	}
	if (tipo.startsWith("image/") && !/hei[cf]/i.test(tipo)) {
		return "imagen";
	}
	if (tipo === "application/pdf" || (!tipo && /\.pdf$/i.test(nombre))) {
		return "pdf";
	}
	if (!tipo && /\.(jpe?g|png|webp|gif)$/i.test(nombre)) {
		return "imagen";
	}
	if (OFICINA_EXT.test(nombre) || OFICINA_MIME.test(tipo)) {
		return "oficina";
	}
	return "otro";
};

interface Estado { archivo: ArchivoParaVer; nombre: string; url: string | null; clase: Clase; error: string | null;
	/** Abierto en WappCell / WappDocs: el archivo ya quedó en WappDrive con este id. */
	editor?: { id: number; campus: number } | null; abriendo?: boolean; }

/** El de al lado en la galería (si hay), conservando la galería. */
const mover = (s: Estado, paso: number): Estado => {
	const g = s.archivo.galeria;
	if (!g || g.length < 2) {
		return s;
	}
	const i = Math.min(g.length - 1, Math.max(0, (s.archivo.indice || 0) + paso));
	if (i === (s.archivo.indice || 0)) {
		return s;
	}
	return inicial({ ...g[i], galeria: g, indice: i });
};

const inicial = (archivo: ArchivoParaVer): Estado => ({ archivo, nombre: archivo.nombre, url: archivo.urlLista || null,
	clase: archivo.urlLista ? (archivo.video ? "video" : "imagen") : "otro", error: null });

/** Se monta UNA vez (en App). */
export const VisorDeArchivos: React.FC = () => {
	const [estado, setEstado] = useState<Estado | null>(null);
	// Descargar a la computadora solo quien puede sacar archivos de WAPP; los demás lo guardan en su WappDrive (Esteban, 2026-10-09).
	const [puedeBajar, setPuedeBajar] = useState<boolean>(false);
	const [guardando, setGuardando] = useState<boolean>(false);
	// pdf.js could not draw this PDF on the phone: back to the "Abrir PDF" button.
	const [pdfSinPaginas, setPdfSinPaginas] = useState<boolean>(false);
	const pdfFallo = useCallback(() => setPdfSinPaginas(true), []);

	useEffect(() => {
		const abrir = (e: Event) => {
			const archivo = (e as CustomEvent<ArchivoParaVer>).detail;
			setEstado(inicial(archivo));
		};
		// Si se navega (p. ej. "Abrir" en el aviso de Guardado en WappDrive), el visor se cierra.
		const cerrarAlNavegar = () => setEstado(null);
		window.addEventListener(EVENTO, abrir);
		window.addEventListener("popstate", cerrarAlNavegar);
		return () => { window.removeEventListener(EVENTO, abrir); window.removeEventListener("popstate", cerrarAlNavegar); };
	}, []);

	// Esc cierra.
	const abierto = !!estado;
	const enEditor = !!(estado && estado.editor);
	useEffect(() => { if (abierto) puedeDescargar().then(setPuedeBajar); }, [abierto]);
	useEffect(() => {
		if (!abierto) {
			return;
		}
		const tecla = (e: KeyboardEvent) => {
			if (enEditor) { return; } // el editor se cierra con su propia ×, que también deja bien la dirección
			if (e.key === "Escape") { setEstado(null); }
			if (e.key === "ArrowRight" || e.key === "ArrowLeft") { setEstado((s) => s ? mover(s, e.key === "ArrowRight" ? 1 : -1) : s); }
		};
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, [abierto, enEditor]);

	const archivoActual = estado ? estado.archivo : null;
	useEffect(() => {
		setPdfSinPaginas(false);
		if (!archivoActual || archivoActual.urlLista) {
			return;
		}
		let vivo = true;
		const directa = archivoActual.obtenerUrl;
		const claseDirecta = archivoActual.video ? "video" : claseDe(archivoActual.mime || "", archivoActual.nombre);
		if (directa && (claseDirecta === "video" || claseDirecta === "imagen")) {
			directa().then((u) => { if (vivo) setEstado((s) => s && s.archivo === archivoActual ? { ...s, url: u, clase: claseDirecta } : s); },
				() => { if (vivo) setEstado((s) => s && s.archivo === archivoActual ? { ...s, error: "No se pudo abrir el archivo." } : s); });
			return () => { vivo = false; };
		}
		let creada: string | null = null;
		archivoActual.cargar().then((cargado) => {
			if (!vivo) {
				return;
			}
			const blobOriginal = cargado instanceof Blob ? cargado : cargado.blob;
			const nombre = (!(cargado instanceof Blob) && cargado.nombre) || archivoActual.nombre;
			const clase = claseDe(blobOriginal.type || "", nombre);
			// Sin el tipo en el Blob el navegador no sabe enseñar el PDF y lo baja.
			const blob = clase === "pdf" && blobOriginal.type !== "application/pdf" ? new Blob([blobOriginal], { type: "application/pdf" }) : blobOriginal;
			creada = URL.createObjectURL(blob);
			setEstado((s) => s && s.archivo === archivoActual ? { ...s, url: creada, clase, nombre, abriendo: clase === "oficina" } : s);
			if (clase === "oficina") {
				// Directo a WappCell / WappDocs: se guarda en su WappDrive y se abre ahí mismo, sin otra ventana.
				guardarParaVer(blob, nombre, archivoActual.origen || "Archivos de WAPP").then((x) => {
					if (vivo) setEstado((s) => s && s.archivo === archivoActual ? { ...s, abriendo: false, editor: x && x.editor ? { id: x.id, campus: x.campus } : null } : s);
				}, () => { if (vivo) setEstado((s) => s && s.archivo === archivoActual ? { ...s, abriendo: false } : s); });
			}
		}, () => {
			if (vivo) {
				setEstado((s) => s && s.archivo === archivoActual ? { ...s, error: "No se pudo abrir el archivo." } : s);
			}
		});
		return () => {
			vivo = false;
			if (creada) {
				const u = creada;
				window.setTimeout(() => URL.revokeObjectURL(u), 60000); // por si quedó abierto aparte
			}
		};
	}, [archivoActual]);

	if (!estado) {
		return null;
	}
	const cerrar = () => setEstado(null);
	if (estado.editor) {
		return (
			<Suspense fallback={<div className="waVisor" role="dialog"><div className="waVisorCaja">Abriendo…</div></div>}>
				<EditorOficina campusId={estado.editor.campus} id={estado.editor.id} onCerrar={cerrar} />
			</Suspense>
		);
	}
	const { nombre, url, clase, error } = estado;
	const g = estado.archivo.galeria;
	const i = estado.archivo.indice || 0;
	const ir = (paso: number) => (e: React.MouseEvent) => { e.stopPropagation(); setEstado((s) => s ? mover(s, paso) : s); };
	const pdfEmbebido = clase === "pdf" && !esTelefono();
	const nada = (e: React.MouseEvent) => e.stopPropagation();
	// El archivo para descargar o guardar: el que ya se ve (blob: o URL firmada) o, si no se puede leer, el original.
	const guardar = async () => {
		const archivo = estado.archivo;
		setGuardando(true);
		try {
			let blob: Blob | null = null;
			try { blob = url ? await (await fetch(url)).blob() : null; } catch { blob = null; }
			if (!blob) { const c = await archivo.cargar(); blob = c instanceof Blob ? c : c.blob; }
			await entregarArchivo(blob, nombre, archivo.origen || "Archivos de WAPP");
		} catch {
			setEstado((s) => s && s.archivo === archivo ? { ...s, error: "No se pudo guardar el archivo." } : s);
		} finally { setGuardando(false); }
	};
	return (
		<div className="waVisor" role="dialog" aria-label={nombre} onClick={cerrar}>
			<div className="waVisorBarra" onClick={nada}>
				<span className="waVisorNombre" title={nombre}>{nombre}{g && g.length > 1 ? <small className="waVisorCuenta"> {i + 1} de {g.length}</small> : null}</span>
				{/* Fotos y videos (WappTeam, WappChat…) no se sacan de WAPP (Esteban, 2026-10-13): el botón solo sale a quien tiene
				    permiso de descargar. Los documentos siguen con "Guardar en WappDrive". */}
				{url && estado.archivo.descargar !== false && (puedeBajar || (clase !== "imagen" && clase !== "video")) ? (
					<button type="button" className="waVisorAccion" disabled={guardando} onClick={guardar}>
						<WappIcono n="download" size={16} />{puedeBajar ? "Descargar a la Computadora" : "Guardar en WappDrive"}</button>
				) : null}
				<button type="button" className="waVisorCerrar" onClick={cerrar} aria-label="Cerrar">×</button>
			</div>
			{error ? <div className="waVisorCaja" onClick={nada}>{error}</div>
				: !url ? <div className="waVisorCaja" onClick={nada}>Cargando…</div>
				: estado.abriendo ? <div className="waVisorCaja" onClick={nada}>Abriendo {/\.(docx?|odt|rtf)$/i.test(nombre) ? "en WappDocs" : /\.(pptx?|odp)$/i.test(nombre) ? "en WappDeck" : "en WappCell"}…</div>
				: clase === "imagen" ? <img src={url} alt={nombre} onClick={nada} />
				: clase === "video" ? <video className="waVisorVideo" src={url} controls controlsList="nodownload" autoPlay playsInline onClick={nada} onContextMenu={(e) => e.preventDefault()} />
				: pdfEmbebido ? <iframe className="waVisorPdf" src={puedeBajar ? url : url + "#toolbar=0"} title={nombre} onClick={nada} />
				: clase === "pdf" && !pdfSinPaginas ? <PaginasPdf key={url} url={url} onFallo={pdfFallo} />
				: (
					<div className="waVisorCaja" onClick={nada}>
						{/* Sin vista previa: abrirlo aparte lo bajaría a la computadora, así que pasa por la regla de descargas.
						    El PDF en teléfono sí se abre (el teléfono no sabe incrustarlo). */}
						{clase === "pdf"
							? <a className="btn btn-primary" href={url} target="_blank" rel="noopener noreferrer">Abrir PDF</a>
							: puedeBajar
							// Descargar con su nombre: la liga blob: de antes bajaba un archivo sin nombre ni extensión.
							? <><p style={{ margin: 0 }}>Este archivo no tiene vista previa.</p>
								<button type="button" className="btn btn-primary" disabled={guardando} onClick={guardar}>Descargar</button></>
							: <><p style={{ margin: 0 }}>Este archivo no tiene vista previa.</p>
								<button type="button" className="btn btn-primary" disabled={guardando || estado.archivo.descargar === false} onClick={guardar}>Guardar en WappDrive</button></>}
					</div>
				)}
			{g && g.length > 1 && i > 0 ? <button type="button" className="waVisorPaso ant" onClick={ir(-1)} aria-label="Anterior">‹</button> : null}
			{g && g.length > 1 && i < g.length - 1 ? <button type="button" className="waVisorPaso sig" onClick={ir(1)} aria-label="Siguiente">›</button> : null}
		</div>
	);
};

export default VisorDeArchivos;
